<template>
  <v-dialog v-model="visible" persistent max-width="638">
    <v-card class="v-dialog--custom" v-if="visible">
      <v-card-title class="headline pa-4">
        <span class="v-dialog--custom__title my-2">{{ $t(`${namespace}.title`) }}</span>
        <v-btn class="v-dialog--custom__close mr-2" fab flat color="gray-6" @click="close()">
          <v-icon dark>close</v-icon>
        </v-btn>
      </v-card-title>

      <div class="section section--border-b">
        <v-card-text class="pt-0 px-4">
          <p>{{ $t(`${namespace}.text`) }}</p>

          <div class="mb-4">
            <input
              ref="input"
              :placeholder="$t(`${namespace}.placeholder`)"
              autocomplete="off"
              class="input--custom"
              @focus="handleInitAutocomplete"
            />
          </div>

          <div
            v-if="isLoading"
            class="text-xs-center py-5"
          >
            <div class="progress">
              <v-progress-circular indeterminate color="accent" />
            </div>
          </div>

          <div v-if="isSuccess">
            <EmptyState
              v-if="!table.body || !table.body.length"
              image="phone"
              :text="$t(`${namespace}.emptyState`)"
              :imageSize="120"
              class="py-4"
            />

            <v-data-table
              v-else
              :headers="tableHeader"
              :items="table.body"
              :pagination.sync="pagination"
              hide-actions
              color="accent"
              no-results-text=""
              no-data-text=""
              :loading="tableLoading"
            >
              <template v-slot:items="props">
                <td style="cursor: pointer" @click="handleSetAddress(props.item)">
                  {{ props.item.address }}
                </td>
                <td>
                  <v-tooltip slot="append" top>
                    <v-btn
                      color="grey"
                      class="mx-0"
                      slot="activator"
                      icon
                      small
                      dark
                      flat
                      @click="handleRemoveAddress(props.item)"
                    >
                      <v-icon small>close</v-icon>
                    </v-btn>
                    <span>{{ $t('Common.remove') }}</span>
                  </v-tooltip>
                </td>
              </template>
            </v-data-table>

            <div v-if="pagination.totalItems > pagination.rowsPerPage" class="text-xs-center mt-3">
              <v-pagination
                v-model="pagination.page"
                :length="pages"
                color="accent"
              />
            </div>
          </div>
        </v-card-text>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { isObject, isArray, objectToArray } from 'utils/functions'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import EmptyState from 'components/EmptyState'

export default {
  name: 'FavoriteAddressDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    kind: {
      type: String
    }
  },
  components: {
    EmptyState
  },
  computed: {
    ...mapState({
      legacyUserId: ({ session }) => session.user.business.legacyUserId
    }),

    isLoading () {
      return this.currentStatus === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.currentStatus === STATUS_SUCCESS
    },

    isFailed () {
      return this.currentStatus === STATUS_FAILED
    },

    pages () {
      return this.pagination.rowsPerPage ? Math.ceil(this.table.body.length / this.pagination.rowsPerPage) : 0
    },

    totalVisible () {
      return window.innerWidth <= 420 ? 5 : 7
    },

    tableHeader () {
      return objectToArray(this.$t('Views.NewWebCallPage.favoriteAddressDialog.table.headers'))
    }
  },
  data () {
    return {
      namespace: 'Views.NewWebCallPage.favoriteAddressDialog',
      favoriteAddressList: [],
      currentStatus: null,
      tableLoading: false,

      // table
      pagination: {
        descending: false,
        page: 1,
        rowsPerPage: 6
      },
      table: {
        body: []
      }
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    close (val) {
      this.$emit('close', val)
    },

    isValidObject (val) {
      return isObject(val)
    },

    isValidArray (val) {
      return isArray(val)
    },

    async handleInitAutocomplete () {
      const autocomplete = new window.google.maps.places.Autocomplete(this.$refs.input)

      autocomplete.addListener('place_changed', async () => {
        try {
          const place = autocomplete.getPlace()
          const positionData = this.isValidObject(place)
            ? {
              address: place.formatted_address,
              number: this.$gmaps.getStreetNumber(place),
              latitude: this.$gmaps.getCoordinateValue(place.geometry.location.lat),
              longitude: this.$gmaps.getCoordinateValue(place.geometry.location.lng)
            } : null

          await this.createFavoriteAddress(positionData)
          this.gaEventDispatch('create')
          this.clearField(this.$refs.input)
        } catch (error) {
          throw new Error(error)
        }
      })
    },

    clearField (elem) {
      elem.value = ''
    },

    handleSetAddress (address) {
      this.close({ ref: this.kind, ...address })
      this.gaEventDispatch('set')
    },

    async handleRemoveAddress (address) {
      await this.removeFavoriteAddress(address)
      this.gaEventDispatch('remove')
    },

    gaEventDispatch (val) {
      this.$ga.event('webcall favorite locale', 'click', val)
    },

    async getFavoriteAddress () {
      this.tableLoading = true

      try {
        const { data: res } = await this.$apiService.getFavoriteAddress(this.legacyUserId)

        if (res && !res.valid) {
          const message = this.isValidArray(res.reasons)
            ? res.reasons[0]
            : this.$t('Common.genericError')

          this.showAlert({
            type: 'error',
            message,
            timeout: 6000
          })

          return
        }

        this.table.body = res && res.content ? [...res.content] : []
        this.currentStatus = STATUS_SUCCESS
      } catch (error) {
        console.log('error', error)
        this.currentStatus = STATUS_FAILED
        this.showAlert({
          type: 'error',
          message: this.$t('Common.genericError'),
          timeout: 6000
        })
      }

      this.tableLoading = false
    },

    async createFavoriteAddress (address) {
      if (!this.isValidObject(address)) return

      try {
        const { data: res } = await this.$apiService.createFavoriteAddress(this.legacyUserId, address)

        if (res && !res.valid) {
          const message = this.isValidArray(res.reasons) ? res.reasons[0] : this.$t('Common.genericError')

          this.showAlert({
            type: 'info',
            message,
            timeout: 6000
          })

          return
        }

        this.getFavoriteAddress()
      } catch (error) {
        console.log('error', error)
        this.showAlert({
          type: 'error',
          message: this.$t('Common.genericError'),
          timeout: 6000
        })
      }
    },

    async removeFavoriteAddress (address) {
      try {
        const { data: res } = await this.$apiService.removeFavoriteAddress(this.legacyUserId, address)

        if (res && !res.valid) {
          throw new Error()
        }

        this.getFavoriteAddress()
      } catch (error) {
        console.log('error', error)
        this.showAlert({
          type: 'error',
          message: this.$t('Common.genericError'),
          timeout: 6000
        })
      }
    }
  },
  watch: {
    visible (val) {
      // fix validation error when close dialog
      this.$validator._paused = !val

      if (val) {
        this.currentStatus = STATUS_IS_LOADING
        this.getFavoriteAddress()
      }
    },

    'table.body' (val) {
      this.pagination.totalItems = val.length
    }
  }
}
</script>

<style lang="scss" scoped>
.input--custom {
  align-items: stretch;
  background-color: #fff;
  border: 1px solid #d6d6d6;
  border-radius: 2px;
  display: block;
  font-size: 14px;
  min-height: 56px;
  outline: none;
  padding: 0 12px;
  transition: border .26s;
  width: 100%;

  &:focus {
    border-color: $accent;
  }
}
</style>

<style lang="scss">
tbody tr:nth-of-type(even) {
  background-color: rgba(0, 0, 0, .05);
}

.th--actions {
  width: 60px;
}
</style>
